import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { CONTROL_STYLE } from '../../constants/invoiceConstants';
import { formatCurrency } from '../../utils/invoiceCalculations';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/apiService';
import image from "../../assets/riyal.png";

/**
 * Component for displaying and managing payment information in the purchase invoice
 * 
 * @param {Object} props - Component props
 * @param {Array} props.payments - Array of payments
 * @param {string} props.paymentMethod - Current payment method
 * @param {string} props.paymentAmount - Current payment amount
 * @param {string} props.paymentReference - Current payment reference
 * @param {number} props.grandTotal - Invoice grand total
 * @param {number} props.totalPaid - Total amount paid
 * @param {number} props.remainingBalance - Remaining balance to be paid
 * @param {Function} props.onPaymentMethodChange - Function to call when payment method changes
 * @param {Function} props.onPaymentAmountChange - Function to call when payment amount changes
 * @param {Function} props.onPaymentReferenceChange - Function to call when payment reference changes
 * @param {Function} props.onAddPayment - Function to call to add a payment
 * @param {Function} props.onRemovePayment - Function to call to remove a payment
 */
const PaymentInformation = ({
  payments = [],
  paymentMethod,
  paymentAmount,
  paymentReference,
  grandTotal,
  totalPaid,
  remainingBalance,
  onPaymentMethodChange,
  onPaymentAmountChange,
  onPaymentReferenceChange,
  onAddPayment,
  onRemovePayment
}) => {
  const [expanded, setExpanded] = useState(true);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentTenant } = useAuth();

  // Fetch payment methods from API
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoading(true);
      try {
        // Use the same endpoint as Sales Invoice
        const response = await api.get('/masters/payment-methods');

        // Check if the API call was successful
        if (response.data && Array.isArray(response.data)) {
          const paymentMethods = response.data;

          // If no payment methods found, use defaults
          if (paymentMethods.length === 0) {
            setAvailableMethods([
              { _id: 'cash_default', name: 'Cash', category: 'cash' },
              { _id: 'card_default', name: 'Credit Card', category: 'card' },
              { _id: 'bank_default', name: 'Bank Transfer', category: 'bank_transfer' },
              { _id: 'mobile_default', name: 'Mobile Wallet', category: 'mobile_wallet' }
            ]);
          } else {
            setAvailableMethods(paymentMethods);
          }
        } else {
          // Fallback to defaults if response format is unexpected
          setAvailableMethods([
            { _id: 'cash_default', name: 'Cash', category: 'cash' },
            { _id: 'card_default', name: 'Credit Card', category: 'card' },
            { _id: 'bank_default', name: 'Bank Transfer', category: 'bank_transfer' },
            { _id: 'mobile_default', name: 'Mobile Wallet', category: 'mobile_wallet' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        // Fallback to default methods on error
        setAvailableMethods([
          { _id: 'cash_default', name: 'Cash', category: 'cash' },
          { _id: 'card_default', name: 'Credit Card', category: 'card' },
          { _id: 'bank_default', name: 'Bank Transfer', category: 'bank_transfer' },
          { _id: 'mobile_default', name: 'Mobile Wallet', category: 'mobile_wallet' }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleAddPayment = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    // Find the selected payment method object
    const selectedMethod = availableMethods.find(m => m._id === paymentMethod) ||
      availableMethods.find(m => m.name === paymentMethod) ||
      { name: paymentMethod || 'Cash', category: 'cash', _id: 'cash_default' };

    onAddPayment({
      method: selectedMethod.name,
      methodId: selectedMethod._id,
      category: selectedMethod.category,
      amount: parseFloat(paymentAmount),
      reference: paymentReference,
      date: new Date()
    });
  };

  // Get icon for payment method based on its category
  const getPaymentIcon = (category) => {
    const iconColor = "primary";
    const iconSize = "small";

    switch (category.toLowerCase()) {
      case 'cash':
        return <MonetizationOnIcon color={iconColor} fontSize={iconSize} />;
      case 'card':
        return <CreditCardIcon color={iconColor} fontSize={iconSize} />;
      case 'bank_transfer':
        return <AccountBalanceIcon color={iconColor} fontSize={iconSize} />;
      case 'mobile_wallet':
        return <SmartphoneIcon color={iconColor} fontSize={iconSize} />;
      default:
        return <PaymentsIcon color={iconColor} fontSize={iconSize} />;
    }
  };

  // Group payment methods by category
  const methodsByCategory = availableMethods.reduce((acc, method) => {
    if (!acc[method.category]) {
      acc[method.category] = [];
    }
    acc[method.category].push(method);
    return acc;
  }, {});

  // Create category labels
  const categoryLabels = {
    cash: 'Cash',
    card: 'Card',
    bank_transfer: 'Bank Transfer',
    mobile_wallet: 'Mobile Wallet',
    other: 'Other'
  };

  // Format the date for display in the payment history
  const formatPaymentDate = (date) => {
    if (!date) return 'N/A';

    try {
      if (date instanceof Date) {
        return date.toLocaleDateString();
      } else if (typeof date === 'string') {
        return new Date(date).toLocaleDateString();
      } else {
        return 'N/A';
      }
    } catch (e) {
      // If date is invalid or causes any other error
      return 'N/A';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#f8faff',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#fff',
          cursor: 'pointer',
        }}
        onClick={handleToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AttachMoneyIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Payment Information
            </Typography>
            {payments.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {payments.length} {payments.length === 1 ? 'payment' : 'payments'} recorded
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand();
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <Box sx={{ p: 2 }}>
          {/* Payment Form */}
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.09)'
            }}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small" sx={CONTROL_STYLE}>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentMethod || ''}
                    onChange={(e) => onPaymentMethodChange(e.target.value)}
                    label="Payment Method"
                  >
                    {loading ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading...
                      </MenuItem>
                    ) : (
                      Object.keys(methodsByCategory).sort().map(category => [
                        <MenuItem
                          key={`category-${category}`}
                          disabled
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: 'text.secondary'
                          }}
                        >
                          {categoryLabels[category] || category}
                        </MenuItem>,
                        ...methodsByCategory[category].map(method => (
                          <MenuItem key={method._id} value={method._id} sx={{ pl: 3 }}>
                            {getPaymentIcon(method.category)}
                            {method.name}
                          </MenuItem>
                        ))
                      ]).flat()
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Amount"
                  value={paymentAmount || ''}
                  onChange={(e) => onPaymentAmountChange(e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                  sx={CONTROL_STYLE}
                  InputProps={{
                    inputProps: { min: 0, step: 0.01 },
                    endAdornment: <InputAdornment position="end">
                      <img src={image} style={{ width: '15px', height: '15px' }} />
                    </InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Reference"
                    value={paymentReference || ''}
                    onChange={(e) => onPaymentReferenceChange(e.target.value)}
                    placeholder="Optional"
                    fullWidth
                    size="small"
                    sx={CONTROL_STYLE}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddPayment}
                    size="small"
                    sx={{
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: 0,
                      ml: 0.5,
                      px: 1.5
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Payment History */}
          {payments.length > 0 && (
            <Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Payment History
                </Typography>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: '#e8f4ff',
                  borderRadius: 20,
                  border: '1px solid rgba(33, 150, 243, 0.2)'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Total Paid:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {totalPaid.toFixed(2)} SAR
                  </Typography>
                </Box>
              </Box>

              <Box sx={{
                maxHeight: '200px',
                overflow: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                },
              }}>
                <Stack spacing={1}>
                  {payments.map((payment, index) => (
                    <Box
                      key={payment.id || index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: '#f5f9ff'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getPaymentIcon(payment.category || 'cash')}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.method}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPaymentDate(payment.date)} {payment.reference && `• ${payment.reference}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {parseFloat(payment.amount).toFixed(2)} SAR
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemovePayment(payment.id)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Balance Info */}
              {totalPaid < grandTotal && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    p: 1.5,
                    backgroundColor: '#fff8e1',
                    borderRadius: '4px',
                    border: '1px dashed #ffd54f'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon color="warning" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Remaining Balance
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                    {remainingBalance.toFixed(2)} SAR
                  </Typography>
                </Box>
              )}

              {/* Change Info */}
              {totalPaid > grandTotal && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    p: 1.5,
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    border: '1px dashed #a5d6a7'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon color="success" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Change Amount
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    {(totalPaid - grandTotal).toFixed(2)} SAR
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {payments.length === 0 && (
            <Box sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px dashed rgba(0, 0, 0, 0.15)',
            }}>
              <PaymentsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                No payments recorded yet
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Add a payment using the form above
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default PaymentInformation;
